// session/mappers.ts — DB-to-domain mapping functions

import type {
    HabitCheckInType,
    HabitFrequencyType,
    HabitLogEntry,
    HabitPeriodUnit,
} from '@shared/types/domain-types'
import { DEFAULT_STATE } from './types'
import { normalizeLinkedHabitIds, normalizeTaskPriority } from './normalizers'

export function mapNotificationsFromDb(
    rows: Array<{
        id: string
        type: string
        title: string
        body: string | null
        metadata: unknown
        is_read: boolean
        created_at: string
    }>
) {
    return rows.map((entry) => {
        const metadata =
            entry.metadata && typeof entry.metadata === 'object' && !Array.isArray(entry.metadata)
                ? (entry.metadata as Record<string, unknown>)
                : {}

        return {
            id: entry.id,
            type: entry.type,
            title: entry.title,
            message: entry.body ?? '',
            href: typeof metadata.href === 'string' ? metadata.href : undefined,
            opensKaizen: Boolean(metadata.opensKaizen),
            createdAt: entry.created_at,
            read: Boolean(entry.is_read),
        }
    })
}

export function mapNotificationsFromApi(
    rows: Array<{
        id: string
        type: string
        title: string
        message: string
        data: Record<string, unknown> | null
        readAt: string | null
        createdAt: string
    }>
) {
    return rows.map((entry) => {
        const metadata =
            entry.data && typeof entry.data === 'object' && !Array.isArray(entry.data)
                ? (entry.data as Record<string, unknown>)
                : {}

        return {
            id: entry.id,
            type: entry.type,
            title: entry.title,
            message: entry.message ?? '',
            href: typeof metadata.href === 'string' ? metadata.href : undefined,
            opensKaizen: Boolean(metadata.opensKaizen),
            createdAt: entry.createdAt,
            read: Boolean(entry.readAt),
        }
    })
}

/** @deprecated XP no longer comes from habits. Kept for backwards compat during migration. */
export function mapHabitXpReward(_checkInType: string) {
    return 0
}

export function mapHabitsFromDb(
    rows: Array<{
        id: string
        title: string
        icon: string | null
        check_in_type: string | null
        target_value: number | null
        unit: string | null
        fallback_threshold: number | null
        frequency: string | null
        period_count: number | null
        period_unit: string | null
        notifiable: boolean | null
        reminder_time: string | null
        created_at: string
        is_archived: boolean
        paused_today: boolean | null
        color: string | null
        current_streak: number | null
        goal_id: string | null
        goal_streak: number | null
        goal_reward_coins: number | null
    }>
) {
    return rows.map((entry) => {
        const checkInType: HabitCheckInType =
            entry.check_in_type === 'NORMAL' || entry.check_in_type === 'MEASUREMENT'
                ? entry.check_in_type
                : 'QUANTITATIVE'
        const frequency: HabitFrequencyType = entry.frequency === 'period' ? 'period' : 'daily'

        const parsedTarget = typeof entry.target_value === 'number' ? entry.target_value : Number(entry.target_value ?? 0)

        return {
            id: entry.id,
            title: entry.title,
            icon: entry.icon ?? 'check',
            checkInType,
            currentStreak: entry.current_streak ?? 0,
            targetValue: checkInType === 'MEASUREMENT' ? undefined : parsedTarget > 0 ? parsedTarget : 1,
            unit: entry.unit ?? undefined,
            fallbackThreshold: entry.fallback_threshold ?? undefined,
            frequency,
            periodCount: frequency === 'period' ? (entry.period_count ?? 1) : undefined,
            periodUnit:
                frequency === 'period'
                    ? ((entry.period_unit === 'month' ? 'month' : 'week') as HabitPeriodUnit)
                    : undefined,
            notifiable: entry.notifiable ?? true,
            notificationTime: entry.reminder_time ? entry.reminder_time.slice(0, 5) : undefined,
            createdAt: entry.created_at,
            archived: entry.is_archived,
            pausedToday: entry.paused_today ?? false,
            color: entry.color ?? 'var(--primary)',
            goalId: entry.goal_id ?? undefined,
            goalStreak: entry.goal_streak ?? undefined,
            goalRewardCoins: entry.goal_reward_coins ?? undefined,
        }
    })
}

export function mapHabitLogsFromDb(
    rows: Array<{
        habit_id: string
        date_key: string
        status: string
        value: number | null
        metadata: unknown
        updated_at: string
    }>
) {
    const grouped: Record<string, HabitLogEntry[]> = {}

    for (const entry of rows) {
        const metadata =
            entry.metadata && typeof entry.metadata === 'object' && !Array.isArray(entry.metadata)
                ? (entry.metadata as Record<string, unknown>)
                : {}

        const entries = Array.isArray(metadata.entries)
            ? metadata.entries.filter((value): value is number => typeof value === 'number')
            : []

        const log = {
            dateKey: entry.date_key,
            status: entry.status,
            value: Number(entry.value ?? 0),
            entries,
            lastValue: typeof metadata.lastValue === 'number' ? metadata.lastValue : undefined,
            updatedAt: entry.updated_at,
        } as HabitLogEntry

        if (!grouped[entry.habit_id]) {
            grouped[entry.habit_id] = [log]
        } else {
            grouped[entry.habit_id].push(log)
        }
    }

    for (const habitId of Object.keys(grouped)) {
        grouped[habitId].sort((a, b) => {
            const left = (a as unknown as { dateKey?: string }).dateKey ?? ''
            const right = (b as unknown as { dateKey?: string }).dateKey ?? ''
            return left.localeCompare(right)
        })
    }

    return grouped
}

export function mapGoalsFromDb(
    rows: Array<{
        id: string
        title: string
        target_value: number
        unit: string | null
        linked_habit_ids: unknown
        deadline_date: string | null
        icon: string | null
        color: string | null
        created_at: string
        status: string | null
    }>
) {
    return rows
        .filter((entry) => entry.status !== 'archived')
        .map((entry) => ({
            id: entry.id,
            title: entry.title,
            targetValue: Number(entry.target_value ?? 0),
            unit: entry.unit ?? 'un',
            linkedHabitIds: normalizeLinkedHabitIds(entry.linked_habit_ids),
            deadline: entry.deadline_date ? new Date(entry.deadline_date).toISOString() : undefined,
            icon: entry.icon ?? '🎯',
            color: entry.color ?? 'var(--primary)',
            createdAt: entry.created_at,
        }))
}

export function mapProjectsFromDb(
    rows: Array<{
        id: string
        title: string
        due_date: string | null
        color: string | null
        created_at: string
        status: string | null
    }>
) {
    return rows
        .filter((entry) => entry.status !== 'archived')
        .map((entry) => ({
            id: entry.id,
            title: entry.title,
            deadline: entry.due_date ? new Date(entry.due_date).toISOString() : undefined,
            color: entry.color ?? 'var(--primary)',
            createdAt: entry.created_at,
        }))
}

export function mapTasksFromDb(
    rows: Array<{
        id: string
        title: string
        completed: boolean
        due_date: string | null
        project_id: string | null
        project_label: string | null
        priority: string | null
        notes: string | null
        created_at: string
        status: string | null
    }>
) {
    return rows
        .filter((entry) => entry.status !== 'archived')
        .map((entry) => ({
            id: entry.id,
            title: entry.title,
            completed: Boolean(entry.completed),
            dueDate: entry.due_date ?? undefined,
            projectId: entry.project_id ?? undefined,
            project: entry.project_label ?? undefined,
            priority: normalizeTaskPriority(entry.priority),
            description: entry.notes ?? undefined,
            createdAt: entry.created_at,
        }))
}

function normalizeInventoryCatalogId(source: unknown) {
    if (!source || typeof source !== 'object' || Array.isArray(source)) return null
    const value = (source as Record<string, unknown>).catalogId
    return typeof value === 'string' ? value : null
}

export function mapInventoryFromDb(
    rows: Array<{
        quantity: number
        metadata: unknown
        store_items:
        | {
            metadata: unknown
        }
        | Array<{
            metadata: unknown
        }>
        | null
    }>
) {
    const inventory = {
        ...DEFAULT_STATE.inventory,
        petItems: [] as Array<{ id: string; name: string; slot: 'colar' | 'armadura' | 'artefato'; bonus: string; equipped: boolean }>,
        cosmetics: [] as Array<{ id: string; name: string; type: 'micro-skin'; themeName?: string; applied: boolean }>,
        tickets: [] as Array<{ id: string; name: string; description: string; quantity: number }>,
    }

    const ownedEmporiumItemIds: string[] = []

    for (const row of rows) {
        const quantity = Math.max(0, Number(row.quantity ?? 0))
        if (quantity <= 0) continue

        const localMeta = row.metadata && typeof row.metadata === 'object' && !Array.isArray(row.metadata)
            ? (row.metadata as Record<string, unknown>)
            : {}
        const rawStoreItemMeta = Array.isArray(row.store_items)
            ? row.store_items[0]?.metadata
            : row.store_items?.metadata
        const storeMeta =
            rawStoreItemMeta && typeof rawStoreItemMeta === 'object' && !Array.isArray(rawStoreItemMeta)
                ? (rawStoreItemMeta as Record<string, unknown>)
                : {}

        const catalogId = normalizeInventoryCatalogId(localMeta) ?? normalizeInventoryCatalogId(storeMeta)
        if (catalogId) {
            ownedEmporiumItemIds.push(catalogId)
        }

        const rewardSource =
            localMeta.reward && typeof localMeta.reward === 'object' && !Array.isArray(localMeta.reward)
                ? (localMeta.reward as Record<string, unknown>)
                : storeMeta.reward && typeof storeMeta.reward === 'object' && !Array.isArray(storeMeta.reward)
                    ? (storeMeta.reward as Record<string, unknown>)
                    : null

        const rewardType = typeof rewardSource?.type === 'string' ? rewardSource.type : null

        if (rewardType === 'inventory') {
            const key = rewardSource?.key
            const amount = Number(rewardSource?.amount ?? 1)
            if (
                key === 'shields' &&
                Number.isFinite(amount)
            ) {
                inventory[key] += Math.max(1, amount) * quantity
            }
            continue
        }

        const itemCategory = typeof storeMeta.category === 'string' ? storeMeta.category : null

        if (itemCategory === 'pet') {
            const itemId = catalogId || ''
            const itemName = typeof storeMeta.name === 'string' ? storeMeta.name : 'Item de Pet'
            const slot = typeof storeMeta.slot === 'string' ? storeMeta.slot : 'colar'
            const bonus = typeof storeMeta.bonus === 'string' ? storeMeta.bonus : ''
            if (itemId && (slot === 'colar' || slot === 'armadura' || slot === 'artefato')) {
                if (!inventory.petItems.some((entry) => entry.id === itemId)) {
                    inventory.petItems.push({
                        id: itemId,
                        name: itemName,
                        slot,
                        bonus,
                        equipped: Boolean(localMeta.equipped),
                    })
                }
            }
            continue
        }

        if (rewardType === 'pet-item') {
            const itemId = typeof rewardSource?.itemId === 'string' ? rewardSource.itemId : null
            const itemName = typeof rewardSource?.itemName === 'string' ? rewardSource.itemName : 'Item de Pet'
            const slot = rewardSource?.slot
            const bonus = typeof rewardSource?.bonus === 'string' ? rewardSource.bonus : ''
            if (itemId && (slot === 'colar' || slot === 'armadura' || slot === 'artefato')) {
                if (!inventory.petItems.some((entry) => entry.id === itemId)) {
                    inventory.petItems.push({
                        id: itemId,
                        name: itemName,
                        slot,
                        bonus,
                        equipped: Boolean(localMeta.equipped),
                    })
                }
            }
            continue
        }

        if (rewardType === 'cosmetic') {
            const cosmeticId = typeof rewardSource?.cosmeticId === 'string' ? rewardSource.cosmeticId : null
            const cosmeticName = typeof rewardSource?.cosmeticName === 'string' ? rewardSource.cosmeticName : 'Cosmetico'
            const themeName = typeof rewardSource?.themeName === 'string' ? rewardSource.themeName : undefined
            if (cosmeticId) {
                if (!inventory.cosmetics.some((entry) => entry.id === cosmeticId)) {
                    inventory.cosmetics.push({
                        id: cosmeticId,
                        name: cosmeticName,
                        type: 'micro-skin',
                        themeName,
                        applied: Boolean(localMeta.applied),
                    })
                }
            }
            continue
        }

        if (rewardType === 'title') {
            if (catalogId) {
                ownedEmporiumItemIds.push(catalogId)
            }
        }
    }

    return {
        inventory,
        ownedEmporiumItemIds: Array.from(new Set(ownedEmporiumItemIds)),
    }
}
